import React, { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import MetricsChart from "./MetricsChart";
import AdvancedMetrics from "./AdvancedMetrics";
import GeographicDistribution from "./GeographicDistribution";
import PlatformMetrics from "./PlatformMetrics";
import SourceMetrics from "./SourceMetrics";
import CTRMetrics from "./CTRMetrics";
import FileUploader from "./FileUploader";
import DataViewerPopup from "./DataViewerPopup";

export function MetricsChartPage() {
  const { isAdmin } = useOutletContext();
  const [activeTab, setActiveTab] = useState("overview");

  // State for popup
  const [isDataViewerOpen, setIsDataViewerOpen] = useState(false);

  // Structured master data object
  const [masterData, setMasterData] = useState({
    total: {
      clicks: null,
      impressions: null,
      plays: null,
      ctr: null,
    },
    countries: {
      clicks: null,
      impressions: null,
    },
    platforms: {
      clicks: null,
      impressions: null,
    },
    sources: {
      clicks: null,
      impressions: null,
    },
  });

  // Legacy data structure for backward compatibility
  // with existing components while we transition
  const [metricsData, setMetricsData] = useState({
    ctr: null,
    geographic: null,
    platform: null,
    source: null,
    clicks: null,
    impressions: null,
    plays: null,
  });

  // Effect to print master data whenever it changes
  useEffect(() => {
    console.log("Master Data Object Updated:", masterData);
  }, [masterData]);

  const handleDataUpload = (type, data) => {
    console.log(`Received ${type} data with ${data?.length || 0} records`);

    // First update our new master data structure
    const newMasterData = { ...masterData };

    // Map the file type to the correct place in our data structure
    switch (type) {
      case "clicks_total":
        newMasterData.total.clicks = data;
        break;
      case "impressions_total":
        newMasterData.total.impressions = data;
        break;
      case "plays":
        newMasterData.total.plays = data;
        break;
      case "ctr":
        newMasterData.total.ctr = data;
        break;
      case "clicks_countries":
        newMasterData.countries.clicks = data;
        break;
      case "impressions_countries":
        newMasterData.countries.impressions = data;
        break;
      case "clicks_platforms":
        newMasterData.platforms.clicks = data;
        break;
      case "impressions_platforms":
        newMasterData.platforms.impressions = data;
        break;
      case "clicks_sources":
        newMasterData.sources.clicks = data;
        break;
      case "impressions_sources":
        newMasterData.sources.impressions = data;
        break;
      default:
        console.log(`Unknown data type: ${type}`);
    }

    setMasterData(newMasterData);

    // Now update the legacy data structure for backward compatibility
    const newMetricsData = { ...metricsData };

    switch (type) {
      case "clicks_total":
        newMetricsData.clicks = data;
        break;
      case "impressions_total":
        newMetricsData.impressions = data;
        break;
      case "plays":
        newMetricsData.plays = data;
        break;
      case "ctr":
        newMetricsData.ctr = data;
        break;
      case "clicks_countries":
      case "geographic":
        newMetricsData.geographic = data;
        break;
      case "clicks_platforms":
      case "platform":
        newMetricsData.platform = data;
        break;
      case "clicks_sources":
      case "source":
        newMetricsData.source = data;
        break;
      default:
        // For other types we don't need to update the legacy structure
        break;
    }

    setMetricsData(newMetricsData);
  };

  // Check if we have data for different sections
  const hasAnyData = Object.values(masterData).some((category) =>
    Object.values(category).some((data) => data !== null)
  );

  const hasTotalData =
    masterData.total.clicks !== null || masterData.total.impressions !== null;
  const hasCountryData = masterData.countries.clicks !== null;
  const hasPlatformData = masterData.platforms.clicks !== null;
  const hasSourceData = masterData.sources.clicks !== null;
  const hasCTRData =
    masterData.total.clicks !== null && masterData.total.impressions !== null;

  // Check if data is available for country CTR
  const hasCountryCTRData =
    masterData.countries.clicks !== null &&
    (masterData.countries.impressions !== null ||
      masterData.total.impressions !== null);

  const tabs = [
    { id: "overview", label: "Overview", disabled: !hasTotalData },
    {
      id: "geographic",
      label: "Geographic",
      disabled: !hasCountryData,
    },
    { id: "platform", label: "Platform", disabled: !hasPlatformData },
    { id: "source", label: "Source", disabled: !hasSourceData },
    { id: "ctr", label: "CTR", disabled: !hasCTRData },
    { id: "advanced", label: "Advanced", disabled: !hasAnyData },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <MetricsChart
            isAdmin={isAdmin}
            epicCtr={masterData.total.ctr}
            calculatedCtr={true}
            clicksData={masterData.total.clicks}
            impressionsData={masterData.total.impressions}
            playsData={masterData.total.plays}
          />
        );
      case "geographic":
        return (
          <GeographicDistribution
            data={masterData.countries.clicks}
            impressionsData={masterData.countries.impressions}
          />
        );
      case "platform":
        return (
          <PlatformMetrics
            data={masterData.platforms.clicks}
            impressionsData={masterData.platforms.impressions}
          />
        );
      case "source":
        return (
          <SourceMetrics
            data={masterData.sources.clicks}
            impressionsData={masterData.sources.impressions}
          />
        );
      case "ctr":
        return (
          <CTRMetrics
            // For total values
            clicksData={masterData.total.clicks}
            impressionsData={masterData.total.impressions}
            // For categories
            geographicData={masterData.countries.clicks}
            geographicImpressions={masterData.countries.impressions} // New prop for country-specific impressions
            platformData={masterData.platforms.clicks}
            platformImpressions={masterData.platforms.impressions} // New prop for platform-specific impressions
            sourceData={masterData.sources.clicks}
            sourceImpressions={masterData.sources.impressions} // New prop for source-specific impressions
          />
        );
      case "advanced":
        return (
          <AdvancedMetrics
            metricsData={{
              clicks: masterData.total.clicks,
              impressions: masterData.total.impressions,
              plays: masterData.total.plays,
              geographic: masterData.countries.clicks,
              geographicImpressions: masterData.countries.impressions,
              platform: masterData.platforms.clicks,
              platformImpressions: masterData.platforms.impressions,
              source: masterData.sources.clicks,
              sourceImpressions: masterData.sources.impressions,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-200">
            Metrics Dashboard
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            Upload, visualize, and analyze audience metrics
          </p>
        </div>

        <div className="flex w-full sm:w-auto flex-wrap gap-3">
          {/* View Data Button */}
          {hasAnyData && (
            <button
              onClick={() => setIsDataViewerOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-md flex items-center justify-center space-x-2 hover:bg-indigo-700 transition text-sm"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span>View Data Structure</span>
            </button>
          )}
        </div>

        {/* Data Viewer Popup */}
        <DataViewerPopup
          isOpen={isDataViewerOpen}
          onClose={() => setIsDataViewerOpen(false)}
          data={masterData}
        />
      </div>

      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-4 sm:p-6">
        <FileUploader onDataUpload={handleDataUpload} masterData={masterData} />

        {hasAnyData && (
          <>
            <div className="border-b border-gray-700 mt-6 overflow-x-auto">
              <nav className="flex min-w-max space-x-4 sm:space-x-8" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => !tab.disabled && setActiveTab(tab.id)}
                    className={`
                      py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                      ${
                        activeTab === tab.id
                          ? "border-blue-500 text-blue-500"
                          : "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300"
                      }
                      ${
                        tab.disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }
                    `}
                    disabled={tab.disabled}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="mt-6">{renderContent()}</div>
          </>
        )}
      </div>
    </div>
  );
}
